import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn';
import { isInsidePopoverPortal } from './popoverPortal';

export interface DrawerProps {
  open: boolean;
  /** Called when the drawer requests to close (X / overlay / Esc). */
  onClose: () => void;
  title?: ReactNode;
  /** Header right-side content (status pills, actions) — sits left of the close button. */
  extra?: ReactNode;
  /** Sticky footer (e.g. Cancel / Save) — pinned; only the body scrolls. */
  footer?: ReactNode;
  /** Panel width in px (caps to the viewport). */
  width?: number;
  children: ReactNode;
  className?: string;
}

/**
 * Right-side slide-over (replaces antd Drawer) built on Radix Dialog — focus-trapped,
 * Esc / overlay close, accessible, and unmounts its body on close (so panel state resets,
 * like antd `destroyOnClose`). Header (title + extra + close) · scrollable body · optional
 * sticky footer. Styled with the Green Bay design tokens, matching {@link Modal}.
 */
export function Drawer({ open, onClose, title, extra, footer, width = 480, children, className }: DrawerProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[200] bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          // The panel labels itself via Dialog.Title; opt out of the description warning (content is freeform).
          aria-describedby={undefined}
          // A calendar opened from inside the panel renders in the shared popup portal (see
          // popoverPortal.ts) so the scrolling body can't clip it — which puts it OUTSIDE this
          // content node. Picking a date must not read as an outside click and close the panel.
          onPointerDownOutside={(e) => { if (isInsidePopoverPortal(e)) e.preventDefault(); }}
          onInteractOutside={(e) => { if (isInsidePopoverPortal(e)) e.preventDefault(); }}
          className={cn(
            'fixed inset-y-0 right-0 z-[201] flex h-full flex-col border-l border-line bg-surface shadow-pop',
            'w-[calc(100vw-1.5rem)] data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right',
            className,
          )}
          style={{ width: `min(${width}px, calc(100vw - 1.5rem))` }}
        >
          {/* Same tinted header + action shelf as ui/Modal — a drawer and a dialog are the same kind
              of surface and must not disagree about it. (The audit caught this: every ui/Modal
              reported a tinted header, the GRN drawer did not.) No corner radius here: the drawer is
              flush to the viewport edge, so there is no rounded corner for the tint to follow. */}
          <div className="flex items-center justify-between gap-3 border-b border-line bg-linear-to-b from-brand-500/[0.08] to-transparent px-5 py-3.5">
            <Dialog.Title className={cn('min-w-0 truncate type-section-title text-content', title == null && 'sr-only')}>
              {title ?? 'Panel'}
            </Dialog.Title>
            <div className="flex shrink-0 items-center gap-2">
              {extra}
              <Dialog.Close
                className="grid size-8 place-items-center rounded-control text-muted transition-colors hover:bg-surface-2 hover:text-content"
                aria-label="Close"
              >
                <X size={18} />
              </Dialog.Close>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer != null && (
            // Wraps, for the same reason the Modal footer does: three actions do not fit a 390px
            // phone on one line, and without this the leftmost button is simply cut off the edge.
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-2/70 px-5 py-3">{footer}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
